<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Bookmark, Check, Link2, Mail } from 'lucide-svelte';

  import { createShare, shareLink } from '$lib/api/share';
  import { notifications } from '$lib/stores/notifications';

  /**
   * Keeping this selection, and handing it to somebody.
   *
   * # Why the code is made per action, not per opening
   *
   * Opening the menu stores nothing. A code is a row, and a customer who opens
   * a menu and thinks better of it should not leave one behind — nor should the
   * two actions produce two different links for the same selection in one
   * sitting, which is what pre-fetching a code on open would do if they used
   * both.
   *
   * # Two ways out
   *
   * The clipboard, for pasting wherever it is going; and the mail client, which
   * is where most of these end up and which otherwise means composing the same
   * sentence by hand every time. Both start from the same code, so a selection
   * sent by mail and the one pasted into a chat are the same link.
   */

  let open = false;
  let busy = false;
  let copied = false;
  let container: HTMLElement | undefined;

  /** Store the selection and hand its link to whatever wants it. */
  async function withLink(use: (_link: string) => void | Promise<void>) {
    if (busy) return;

    open = false;
    busy = true;
    try {
      await use(shareLink(await createShare()));
    } catch {
      notifications.error($_('share.failed'));
    } finally {
      busy = false;
    }
  }

  function copy() {
    return withLink(async (link) => {
      try {
        await navigator.clipboard.writeText(link);
        copied = true;
        setTimeout(() => (copied = false), 2500);
      } catch {
        // A browser that refuses the clipboard — or a page served without TLS —
        // must still hand the link over, so it is shown to be copied by hand.
        notifications.info(link);
      }
    });
  }

  function byEmail() {
    return withLink((link) => {
      const subject = encodeURIComponent($_('share.mailSubject'));
      const body = encodeURIComponent($_('share.mailBody', { values: { link } }));
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    });
  }

  function onWindowClick(event: MouseEvent) {
    if (!open) return;
    if (container && !container.contains(event.target as Node)) open = false;
  }
</script>

<svelte:window on:click={onWindowClick} on:keydown={(e) => e.key === 'Escape' && (open = false)} />

<div class="relative" bind:this={container}>
  <button
    type="button"
    on:click={() => (open = !open)}
    disabled={busy}
    title={copied ? $_('share.copied') : $_('share.save')}
    class="chrome-control inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control border disabled:cursor-not-allowed disabled:opacity-50"
    aria-haspopup="menu"
    aria-expanded={open}
  >
    {#if copied}
      <!-- White, not the accent. This sits on the navy bar, where accent-600
           is 2.04:1 and a tick nobody can see is not a confirmation. -->
      <Check class="h-4 w-4" />
      <span class="sr-only">{$_('share.copied')}</span>
    {:else}
      <Bookmark class="h-4 w-4" />
      <span class="sr-only">{$_('share.save')}</span>
    {/if}
  </button>

  {#if open}
    <div
      class="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-surface border border-hairline bg-surface-panel shadow-overlay"
      role="menu"
    >
      <button
        type="button"
        role="menuitem"
        on:click={copy}
        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-surface-canvas hover:text-gray-900"
      >
        <Link2 class="h-4 w-4 text-gray-400" />
        {$_('share.copyLink')}
      </button>
      <button
        type="button"
        role="menuitem"
        on:click={byEmail}
        class="flex w-full items-center gap-2 border-t border-hairline px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-surface-canvas hover:text-gray-900"
      >
        <Mail class="h-4 w-4 text-gray-400" />
        {$_('share.byEmail')}
      </button>
    </div>
  {/if}
</div>
